import { useMutation } from '@apollo/client';
import { useSnackbar } from '@components/Snackbar/SnackbarProvider';
import { useAuth } from '@contexts/AuthContext';
import { useProId } from '@contexts/ProIdContext';
import { GET_JOB_OFFER, PUBLISH_JOB_OFFER_DRAFT_PRO, PUBLISH_JOB_OFFER_DRAFT_UPERCUT } from '@gql/jobs';
import { useState } from 'react';

interface UsePublishJobOfferOptions {
    jobOfferId?: string;
    jobNumber?: string;
    proId?: string;
    onSuccess?: (result: any) => void;
    onError?: (error: any) => void;
}

export const usePublishJobOffer = ({ jobOfferId, jobNumber, proId, onSuccess, onError }: UsePublishJobOfferOptions = {}) => {
    const { isUpercut } = useAuth();
    const { isProIdRequired } = useProId();
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(false);

    const isUpercutFlow = isUpercut && !isProIdRequired;

    const refetchQueries = jobNumber
        ? [{ query: GET_JOB_OFFER, variables: { jobNumber, showInactive: true } }]
        : [];

    const [publishDraftPro] = useMutation(PUBLISH_JOB_OFFER_DRAFT_PRO, {
        refetchQueries,
    });

    const [publishDraftUpercut] = useMutation(PUBLISH_JOB_OFFER_DRAFT_UPERCUT, {
        refetchQueries,
    });

    const publish = async () => {
        if (!jobOfferId) return;
        setLoading(true);
        try {
            const mutate = isUpercutFlow ? publishDraftUpercut : publishDraftPro;
            const variables = isUpercutFlow ? { id: jobOfferId, proId } : { id: jobOfferId };
            const { data } = await mutate({ variables });
            const result = isUpercutFlow ? data?.publishJobOfferDraftUpercut : data?.publishJobOfferDraftPro;
            enqueueSnackbar("L'offre a été publiée avec succès.", { variant: 'success' });
            onSuccess?.(result);
        } catch (err) {
            const message = err?.graphQLErrors?.[0]?.message ?? err?.message ?? "Erreur lors de la publication.";
            enqueueSnackbar(message, { variant: 'error' });
            onError?.(err);
        } finally {
            setLoading(false);
        }
    };

    return { publish, loading };
};
